import type { GatewayBrowserClient } from "../gateway.ts";

export type TerminalHistoryEntry = {
  id: string;
  command: string;
  stdout: string;
  stderr: string;
  exitCode: number | null;
  signal: string | null;
  running: boolean;
  error: string | null;
  timestamp: number;
};

export type TerminalState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  terminalInput: string;
  terminalHistory: TerminalHistoryEntry[];
  terminalRunning: boolean;
  terminalPtySessionId: string | null;
  terminalPtyReady: boolean;
};

// Global PTY event listeners (connected via app-gateway event handler)
type PtyEventListener = (event: string, payload: Record<string, unknown>) => void;
const ptyListeners = new Set<PtyEventListener>();

export function addPtyEventListener(listener: PtyEventListener) {
  ptyListeners.add(listener);
}

export function removePtyEventListener(listener: PtyEventListener) {
  ptyListeners.delete(listener);
}

/** Called from app-gateway handleGatewayEventUnsafe for terminal.pty.* events */
export function dispatchPtyEvent(event: string, payload: Record<string, unknown>) {
  for (const listener of ptyListeners) {
    try {
      listener(event, payload);
    } catch {
      // ignore listener errors
    }
  }
}

// PTY session lifecycle
let currentPtyCleanup: (() => void) | null = null;

export async function spawnPtySession(
  state: TerminalState,
  onData: (data: string) => void,
  onExit: (exitCode: number, signal: number) => void,
): Promise<string | null> {
  if (!state.client || !state.connected) return null;

  // Clean up existing session
  if (currentPtyCleanup) {
    currentPtyCleanup();
    currentPtyCleanup = null;
  }

  try {
    const result = (await state.client.request("terminal.pty.spawn", {})) as {
      sessionId: string;
      cols: number;
      rows: number;
    };

    state.terminalPtySessionId = result.sessionId;
    state.terminalPtyReady = true;

    // Listen for PTY events via the global dispatcher
    const listener: PtyEventListener = (event, payload) => {
      if (event === "terminal.pty.output" && payload.sessionId === result.sessionId) {
        onData((payload.data as string) ?? "");
      }
      if (event === "terminal.pty.exit" && payload.sessionId === result.sessionId) {
        state.terminalPtyReady = false;
        state.terminalPtySessionId = null;
        onExit((payload.exitCode as number) ?? 0, (payload.signal as number) ?? 0);
      }
    };

    addPtyEventListener(listener);

    currentPtyCleanup = () => {
      removePtyEventListener(listener);
      if (state.terminalPtySessionId) {
        state.client
          ?.request("terminal.pty.kill", { sessionId: state.terminalPtySessionId })
          .catch(() => {});
      }
      state.terminalPtySessionId = null;
      state.terminalPtyReady = false;
    };

    return result.sessionId;
  } catch (err) {
    state.terminalPtyReady = false;
    state.terminalPtySessionId = null;
    console.error("PTY spawn failed:", err);
    return null;
  }
}

export async function writePtyInput(state: TerminalState, data: string): Promise<void> {
  if (!state.client || !state.connected || !state.terminalPtySessionId) return;
  try {
    await state.client.request("terminal.pty.input", {
      sessionId: state.terminalPtySessionId,
      data,
    });
  } catch {
    // Connection lost
  }
}

export async function resizePty(state: TerminalState, cols: number, rows: number): Promise<void> {
  if (!state.client || !state.connected || !state.terminalPtySessionId) return;
  try {
    await state.client.request("terminal.pty.resize", {
      sessionId: state.terminalPtySessionId,
      cols,
      rows,
    });
  } catch {
    // Connection lost
  }
}

export function killPtySession(state: TerminalState): void {
  if (currentPtyCleanup) {
    currentPtyCleanup();
    currentPtyCleanup = null;
  }
}

// Legacy exec for quick commands (backward compat)
let entrySeq = 0;

export async function execTerminalCommand(state: TerminalState) {
  if (!state.client || !state.connected) {
    return;
  }
  const command = state.terminalInput.trim();
  if (!command) {
    return;
  }

  const id = `cmd-${++entrySeq}-${Date.now()}`;
  const entry: TerminalHistoryEntry = {
    id,
    command,
    stdout: "",
    stderr: "",
    exitCode: null,
    signal: null,
    running: true,
    error: null,
    timestamp: Date.now(),
  };

  state.terminalHistory = [...state.terminalHistory, entry];
  state.terminalInput = "";
  state.terminalRunning = true;

  const inputEl = document.getElementById("term-input") as HTMLInputElement | null;
  if (inputEl) {
    inputEl.value = "";
    inputEl.focus();
  }

  scrollTerminalToBottom();

  try {
    const result = (await state.client.request("terminal.exec", {
      command,
      timeout: 60_000,
    })) as {
      stdout: string;
      stderr: string;
      exitCode: number | null;
      signal: string | null;
    };

    const updated = state.terminalHistory.map((e) =>
      e.id === id
        ? {
            ...e,
            stdout: result.stdout,
            stderr: result.stderr,
            exitCode: result.exitCode,
            signal: result.signal,
            running: false,
          }
        : e,
    );
    state.terminalHistory = updated;
    scrollTerminalToBottom();
  } catch (err) {
    const updated = state.terminalHistory.map((e) =>
      e.id === id
        ? {
            ...e,
            error: String(err),
            running: false,
          }
        : e,
    );
    state.terminalHistory = updated;
  } finally {
    state.terminalRunning = false;
    requestAnimationFrame(() => {
      const el = document.getElementById("term-input") as HTMLInputElement | null;
      if (el && !el.disabled) el.focus();
    });
  }
}

export function clearTerminalHistory(state: TerminalState) {
  state.terminalHistory = [];
}

function scrollTerminalToBottom() {
  requestAnimationFrame(() => {
    const el = document.getElementById("term-output");
    if (el) el.scrollTop = el.scrollHeight;
  });
}
